import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import warehouseRepository from '../../services/repositories/warehouse.repository';
import '../../styles/products.scss';

const WarehouseIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [error, setError] = useState(null);

  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await warehouseRepository.getAll();
      
      const transformedWarehouses = Array.isArray(response) ? response : (response.data || []);
      
      const formattedWarehouses = transformedWarehouses.map(warehouse => ({
        id: warehouse.id,
        name: warehouse.name,
        status: warehouse.status || 'active',
        max_qte: warehouse.max_qte || '-',
      }));
      
      setWarehouses(formattedWarehouses);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setError(err.message || 'Error loading warehouses');
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'Max Quantity',
      accessor: 'max_qte',
      sortable: true,
    },
    {
      header: 'Status',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/warehouses/warehouse-add');
  }, [navigate]);

  const handleEdit = useCallback((warehouse) => {
    navigate(`/admin/warehouses/warehouse-edit/${warehouse.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (warehouse) => {
    if (window.confirm(`Are you sure you want to delete "${warehouse.name}"?`)) {
      try {
        setLoading(true);
        await warehouseRepository.delete(warehouse.id);
        alert('Warehouse deleted successfully');
        await fetchWarehouses();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Error deleting warehouse');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchWarehouses]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} warehouses?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await warehouseRepository.delete(id);
        }
        alert(`${selectedIds.length} warehouses deleted successfully`);
        await fetchWarehouses();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Error deleting warehouses');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchWarehouses]);

  useEffect(() => {
    setTitle('Warehouses');
    fetchWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="warehouses-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={warehouses}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Warehouses List"
        addButtonText="Add New Warehouse"
        searchPlaceholder="Search warehouses..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default WarehouseIndex;
